/**
 * The customizer's parameter schema -- the one contract every module in the
 * customizer (game/src/customizer/) is written against.  It lives HERE, in
 * @catworld/shared, because a look is a persistence + wire format -- catrecord.js
 * normalizes every record through it -- and moving it down (WS-05) is what
 * made shared the true bottom of the dependency graph.
 *
 * A "look" is a flat object of numbers plus a `base` (which shipped cat it is
 * built on) and a `name`.  Flat because it is what a slider list, a
 * localStorage record and a shader uniform block all want; the grouping that
 * the panel renders lives in GROUPS, not in the data.
 *
 * Two later additions, `coat` and `pattern`, are flat STRINGS rather than
 * numbers.  They started life as an index into COATS/PATTERNS, and that was
 * wrong twice over: an index silently changes meaning the day a swatch is
 * inserted in the middle of the grid, and a stored look full of bare 3s and
 * 5s cannot be read back by a person repairing a cats.json.  A key survives
 * both -- reordering the palette is a UI decision, not a data migration --
 * and normalize() collapses any key it does not recognise to the default, so
 * a look saved by a NEWER client with a swatch this build never shipped
 * degrades to a legal cat instead of poisoning the record.
 *
 * Ranges are chosen so that BOTH extremes are a cat.  A slider you can push
 * into a broken pose is a bug in this file, not in the morph code.  The same
 * bar applies to every COATS x PATTERNS pair: there is no pick, and no pick
 * combined with any slider position, that is not a plausible cat.
 */
const pct = (v) => `${Math.round(v * 100)}%`;
const deg = (v) => `${Math.round(v)}°`;
/** Every slider, in panel order, grouped for display. */
export const GROUPS = [
    {
        title: "Build",
        params: [
            { key: "size", label: "Size", min: 0.70, max: 1.40, step: 0.01, def: 1, fmt: pct },
            { key: "chonk", label: "Chonk", min: 0.75, max: 1.40, step: 0.01, def: 1, fmt: pct },
            { key: "body_length", label: "Body length", min: 0.80, max: 1.30, step: 0.01, def: 1, fmt: pct },
            { key: "leg_length", label: "Leg length", min: 0.75, max: 1.35, step: 0.01, def: 1, fmt: pct },
            { key: "leg_thickness", label: "Leg thickness", min: 0.70, max: 1.45, step: 0.01, def: 1, fmt: pct },
            { key: "neck_length", label: "Neck length", min: 0.70, max: 1.45, step: 0.01, def: 1, fmt: pct },
        ],
    },
    {
        title: "Head",
        params: [
            { key: "head_size", label: "Head size", min: 0.80, max: 1.30, step: 0.01, def: 1, fmt: pct },
            { key: "muzzle", label: "Muzzle", min: 0.70, max: 1.45, step: 0.01, def: 1, fmt: pct },
            { key: "ear_size", label: "Ear size", min: 0.55, max: 1.70, step: 0.01, def: 1, fmt: pct },
            { key: "ear_tilt", label: "Ear tilt", min: -25, max: 35, step: 1, def: 0, fmt: deg },
        ],
    },
    {
        title: "Tail",
        params: [
            { key: "tail_length", label: "Tail length", min: 0.45, max: 1.65, step: 0.01, def: 1, fmt: pct },
            { key: "tail_fluff", label: "Tail fluff", min: 0.60, max: 2.00, step: 0.01, def: 1, fmt: pct },
            { key: "tail_curl", label: "Tail curl", min: -30, max: 30, step: 1, def: 0, fmt: deg },
        ],
    },
    {
        // The palette grid the panel draws above these sliders picks a `coat`
        // (see COATS below); the sliders stay as fine refinement on top of it.
        // coat_hue on its own was the whole colour story once, and it was weak:
        // a hue rotation preserves luma and saturation, so on the shipped coats
        // -- which are mostly low-saturation browns -- the extremes were "a
        // slightly different brown", and the slider read as broken.  It earns
        // its keep again the moment a swatch is chosen, because rotating a
        // saturated ginger actually goes somewhere.
        title: "Coat",
        params: [
            { key: "coat_hue", label: "Colour", min: -180, max: 180, step: 1, def: 0, fmt: deg },
            { key: "coat_sat", label: "Richness", min: 0, max: 1.90, step: 0.01, def: 1, fmt: pct },
            { key: "coat_light", label: "Brightness", min: 0.55, max: 1.55, step: 0.01, def: 1, fmt: pct },
        ],
    },
    {
        // `stripes` is the BAKED markings -- the luminance contrast of whatever
        // pelt.py painted into the atlas.  `pattern_strength` and
        // `pattern_scale` belong to the PICKED pattern (see PATTERNS below):
        // strength is its opacity, scale is feature size where the pattern is a
        // texture (stripe pitch, patch size) and coverage where it is a region
        // (sock height, mask reach).  Scale's range is deliberately narrower
        // than it could be: below 0.55 the tabby collapses into moire against
        // the atlas grain, above 1.8 a sock swallows the shoulder -- both ends
        // as shipped are still recognisably socks and stripes.
        title: "Pattern",
        params: [
            { key: "stripes", label: "Markings", min: 0, max: 2.20, step: 0.01, def: 1, fmt: pct },
            { key: "pattern_strength", label: "Strength", min: 0, max: 1.00, step: 0.01, def: 1, fmt: pct },
            { key: "pattern_scale", label: "Size", min: 0.55, max: 1.80, step: 0.01, def: 1, fmt: pct },
        ],
    },
    {
        title: "Eyes",
        params: [
            { key: "eye_hue", label: "Eye colour", min: -180, max: 180, step: 1, def: 0, fmt: deg },
            { key: "eye_sat", label: "Eye richness", min: 0, max: 2.00, step: 0.01, def: 1, fmt: pct },
            { key: "eye_glow", label: "Eye shine", min: 0, max: 1.00, step: 0.01, def: 0, fmt: pct },
        ],
    },
];
/** Flat list of every Param, in panel order. */
export const PARAMS = GROUPS.flatMap((g) => g.params);
/** Which shipped cat a look is built on.  Indexes main.js's CATS array. */
export const BASES = ["sandy", "ginger", "gray", "toon", "toon-tabby"];
export const COATS = [
    { key: "natural", label: "Natural", hex: null },
    { key: "black", label: "Black", hex: "#221e26" },
    { key: "smoke", label: "Smoke", hex: "#524b54" },
    { key: "seal", label: "Seal", hex: "#3d2d22" },
    { key: "chocolate", label: "Chocolate", hex: "#5e3a25" },
    { key: "red", label: "Red", hex: "#b0521c" },
    { key: "ginger", label: "Ginger", hex: "#d9822e" },
    { key: "cream", label: "Cream", hex: "#eacd9d" },
    { key: "fawn", label: "Fawn", hex: "#c8a67c" },
    { key: "lilac", label: "Lilac", hex: "#b5a3a4" },
    { key: "blue", label: "Blue", hex: "#6e7480" },
    { key: "silver", label: "Silver", hex: "#c9cbce" },
    { key: "white", label: "White", hex: "#f2eee4" },
];
/**
 * The coat patterns, in the order the panel's chips show them.  pelt.js maps
 * the key to a shader branch; the field itself is generated per-fragment
 * from the bind-pose surface point, the same authoring rule pelt.py states
 * for the baked coats, which is why one pattern definition fits both rig
 * families and every UV layout.
 *
 * "natural" -- like the palette's -- means "the base cat's own baked
 * markings, untouched" and is the default old looks land on.  "solid" is a
 * real choice, not a synonym for it: it actively flattens the baked
 * markings (by pattern_strength), so picking Solid on the tabby toon
 * produces a genuinely plain cat instead of doing nothing.
 */
export const PATTERNS = [
    { key: "natural", label: "Natural" },
    { key: "solid", label: "Solid" },
    { key: "tabby", label: "Tabby" },
    { key: "spots", label: "Spots" },
    { key: "tuxedo", label: "Tuxedo" },
    { key: "calico", label: "Calico" },
    { key: "point", label: "Point" },
    { key: "socks", label: "Socks" },
];
const COAT_KEYS = COATS.map((c) => c.key);
const PATTERN_KEYS = PATTERNS.map((p) => p.key);
/** A look with every slider centred, on the default base. */
export function defaultLook() {
    const look = { name: "", base: "sandy", coat: "natural", pattern: "natural" };
    for (const p of PARAMS)
        look[p.key] = p.def;
    return look;
}
/** True when nothing has been moved off its default. */
export function isDefault(look) {
    return look.coat === "natural" && look.pattern === "natural"
        && PARAMS.every((p) => Math.abs(look[p.key] - p.def) < 1e-6);
}
/**
 * Fill in what is missing and clamp what is out of range.
 *
 * Everything that reads a look from outside this process -- localStorage, a
 * shared URL, a preset written by hand -- goes through here, so no other
 * module has to defend against a half-formed one.
 */
export function normalize(raw) {
    const look = defaultLook();
    if (!raw || typeof raw !== "object")
        return look;
    const r = raw;
    for (const p of PARAMS) {
        const v = Number(r[p.key]);
        if (Number.isFinite(v))
            look[p.key] = Math.min(p.max, Math.max(p.min, v));
    }
    if (typeof r.name === "string")
        look.name = r.name.slice(0, 40);
    if (BASES.includes(r.base))
        look.base = r.base;
    // Unrecognised keys keep the defaults set above -- a look from before the
    // palette existed simply lacks these fields, and a look from a build that
    // shipped a swatch this one never heard of degrades to an unpatterned cat
    // rather than to a throw.  includes() on the derived key lists, so adding
    // an entry to COATS/PATTERNS is the whole migration.
    if (COAT_KEYS.includes(r.coat))
        look.coat = r.coat;
    if (PATTERN_KEYS.includes(r.pattern))
        look.pattern = r.pattern;
    return look;
}
/** A random but still cat-shaped look, biased towards the middle of each range. */
export function randomLook(rnd = Math.random) {
    const look = defaultLook();
    for (const p of PARAMS) {
        // Two samples averaged: a triangular distribution, so the extremes are
        // rare rather than as likely as the sensible middle.
        const t = (rnd() + rnd()) / 2;
        const v = p.min + (p.max - p.min) * t;
        look[p.key] = p.step >= 1 ? Math.round(v) : Math.round(v * 100) / 100;
    }
    look.base = BASES[Math.floor(rnd() * BASES.length)];
    // Swatch and pattern are drawn flat, "natural" included: every entry was
    // admitted to the lists on the promise that it is a cat with anything else,
    // so no pick needs to be rarer than another.  pattern_strength has already
    // been rolled above with the triangular bias, which is right here too -- a
    // random calico at 8% strength would read as Random doing nothing, and the
    // bias keeps that corner rare without forbidding it.
    look.coat = COATS[Math.floor(rnd() * COATS.length)].key;
    look.pattern = PATTERNS[Math.floor(rnd() * PATTERNS.length)].key;
    return look;
}
